import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import Auth from '../Auth';
import './HeaderDrawer.scss';


const HamburgerIcon = ({ open }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    className={`HeaderDrawer__icon${open ? ' HeaderDrawer__icon--open' : ''}`}
    aria-hidden="true"
    focusable="false"
  >
    <line x1="3" y1="7"  x2="19" y2="7"  stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="HeaderDrawer__bar HeaderDrawer__bar--top" />
    <line x1="3" y1="11" x2="19" y2="11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="HeaderDrawer__bar HeaderDrawer__bar--mid" />
    <line x1="3" y1="15" x2="19" y2="15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="HeaderDrawer__bar HeaderDrawer__bar--bot" />
  </svg>
);


const HeaderDrawer = ({ isAuthed }) => {
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Close the drawer whenever the route changes. Handles nav-link clicks
  // plus any programmatic history.push that might happen elsewhere.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    // Move focus into the panel for screen-reader / keyboard users.
    if (panelRef.current) panelRef.current.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // Return focus to the trigger when the drawer closes.
      if (triggerRef.current) triggerRef.current.focus();
    };
  }, [open]);

  const handleToggle = () => setOpen((v) => !v);
  const handleClose = () => setOpen(false);

  const handleSignOut = () => {
    setOpen(false);
    Auth.signOut();
  };

  const handleCtaClick = () => {
    setOpen(false);
    history.push(isAuthed ? '/dashboard' : '/start/step1');
  };

  return (
    <div className="HeaderDrawer">
      <button
        ref={triggerRef}
        type="button"
        className="HeaderDrawer__trigger"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="header-drawer-panel"
        onClick={handleToggle}
      >
        <HamburgerIcon open={open} />
      </button>

      <div
        className={`HeaderDrawer__overlay${open ? ' HeaderDrawer__overlay--open' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <aside
        id="header-drawer-panel"
        ref={panelRef}
        tabIndex="-1"
        className={`HeaderDrawer__panel${open ? ' HeaderDrawer__panel--open' : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!open}
      >
        <nav className="HeaderDrawer__nav">
          {isAuthed ? (
            <ul>
              <li><Link to="/dashboard">Dashboard</Link></li>
              <li><Link to="/recipients">Recipients</Link></li>
              <li><Link to="/requests/add">New request</Link></li>
              <li><Link to="/subscription">Subscription</Link></li>
            </ul>
          ) : (
            <ul>
              <li><a href="/#how-it-works" onClick={handleClose}>How it works</a></li>
              <li><a href="/#faqs" onClick={handleClose}>FAQs</a></li>
              <li><Link to="/signin">Sign in</Link></li>
            </ul>
          )}
        </nav>

        <div className="HeaderDrawer__footer">
          <button
            type="button"
            className="HeaderDrawer__cta"
            onClick={handleCtaClick}
          >
            {isAuthed ? 'Go to Dashboard' : 'Get started'}
          </button>
          {isAuthed && (
            <button
              type="button"
              className="HeaderDrawer__signout"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          )}
          <a
            className="HeaderDrawer__contact"
            href="mailto:hello@meetwabi.com"
            onClick={handleClose}
          >
            hello@meetwabi.com
          </a>
        </div>
      </aside>
    </div>
  );
};

export default HeaderDrawer;
